import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { formatAmount, formatDateTime } from '@/lib/venue/format'
import type { VenueSettlementDeductionsView } from '@/lib/venue/settlementTypes'
import { DEDUCTION_POLICY_NOTICE, overDeductedNoticeText } from '@/lib/venue/settlementViewModel'

/**
 * 분쟁환불 차감 내역(설계 §2) — 차감 건수·총 차감 금액·차감 후 정산 예정 금액 + 건별 목록.
 *
 * 🚨 `settlementAmount`는 음수 그대로 렌더한다(`formatAmount`가 이미 음수를 그대로 포맷한다) —
 * 0으로 뭉개면 회수해야 할 금액이 장부에서 사라진다(api-spec §4).
 */
export function SettlementDeductionListTable({
  data,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  data: VenueSettlementDeductionsView
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}) {
  const overDeductedNotice = overDeductedNoticeText(data)

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader title={`${data.cycleMonth} 회차 차감 내역`} description={DEDUCTION_POLICY_NOTICE} />
        <CardBody>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <dt className="text-xs text-slateish-600">차감 건수</dt>
              <dd className="mt-1 text-lg font-semibold text-ink-700">
                {data.deductionCount.toLocaleString('ko-KR')}건
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slateish-600">총 차감 금액</dt>
              <dd className="mt-1 text-lg font-semibold text-danger-500">-{formatAmount(data.deductionAmount)}</dd>
            </div>
            <div>
              <dt className="text-xs text-slateish-600">차감 후 정산 예정 금액</dt>
              {/* 🚨 음수 그대로 — 0으로 뭉개지 않는다. */}
              <dd className="mt-1 text-lg font-semibold text-ink-700">{formatAmount(data.settlementAmount)}</dd>
            </div>
          </dl>
        </CardBody>
      </Card>

      {overDeductedNotice ? <Alert tone="warning">{overDeductedNotice}</Alert> : null}

      {data.deductions.length === 0 ? (
        <EmptyState title="이번 회차에 차감 내역이 없습니다" />
      ) : (
        <div className="space-y-3">
          <div className="overflow-x-auto rounded-xl border border-slateish-200">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slateish-200 bg-slateish-50 text-left text-xs font-medium text-slateish-600">
                  <th scope="col" className="whitespace-nowrap px-3 py-2">
                    처리 일시
                  </th>
                  <th scope="col" className="whitespace-nowrap px-3 py-2">
                    사유
                  </th>
                  <th scope="col" className="whitespace-nowrap px-3 py-2">
                    차감 금액
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.deductions.map((item) => (
                  <tr key={item.refundId} className="border-b border-slateish-200 last:border-b-0">
                    <td className="whitespace-nowrap px-3 py-2 text-slateish-600">
                      {formatDateTime(item.occurredAt)}
                    </td>
                    {/* 🚨 라벨은 서버가 만든 값 그대로 — 코드↔문구 표를 화면이 따로 두지 않는다. */}
                    <td className="px-3 py-2 text-ink-700">{item.label}</td>
                    <td className="px-3 py-2 text-right text-danger-500">-{formatAmount(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasNextPage ? (
            <div className="flex justify-center">
              <Button variant="secondary" size="sm" disabled={isFetchingNextPage} onClick={onLoadMore}>
                {isFetchingNextPage ? '불러오는 중' : '더 보기'}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
